import d2cColors from '@foxcorp-product/foxdtc-tokens/dist/webapps/colors.tailwind';
import typography from '@tailwindcss/typography';
import type { Config } from 'tailwindcss';
import tailwindAnimate from 'tailwindcss-animate';
import plugin from 'tailwindcss/plugin';

const config = {
  darkMode: ['class'],
  content: ['./pages/**/*.{ts,tsx}', './components/**/*.{ts,tsx}', './app/**/*.{ts,tsx}', './src/**/*.{ts,tsx}'],

  // Making sure tailwind generates these classes - min-[376px] for AccountHeader
  safelist: [
    'min-[376px]',
    // Making sure tailwind generates grid-cols classes for Grid
    {
      pattern: /^grid-cols-[1-9]$/,
      variants: ['xs', 'xsm', 'sm', 'md', 'lg', 'xl', '2xl'],
    },
    // Making sure tailwind generates gap classes for Grid
    {
      pattern: /^gap-[1-9]$/,
      variants: ['xs', 'xsm', 'sm', 'md', 'lg', 'xl', '2xl'],
    },
  ],
  prefix: '',
  theme: {
    container: {
      padding: {
        DEFAULT: '16px',
        xs: '16px',
        sm: '40px',
        lg: '40px',
        '2xl': '0',
      },
      screens: {
        xs: '375px',
        '2xsm': '744px', // ipad mini
        sm: '768px', // ipad
        md: '1024px',
        lg: '1280px',
        xl: '1680px',
        '2xl': '1920px',
      },
    },
    screens: {
      xs: '375px', // mobile
      xsm: '480px', // large mobile
      '2xsm': '744px', // ipad mini
      sm: '768px', // ipad
      smmid: '896px', // mlp tablet
      md: '1024px', // desktop sm
      lg: '1280px', // desktop md
      lgmid: '1440px',
      '2lg': '1512px',
      xl: '1680px', // desktop lg
      xlmid: '1728px',
      '2xl': '1920px',
    },
    extend: {
      colors: {
        // Design System Colors
        ...d2cColors,
        // Brand Colors
        primary: '#FF4E00',
        secondary: '#041129',

        // UI Colors
        darkNavy: '#111B25',
        deepNavy: '#151B26',
        midnightNavy: '#222835',
        navyAccent: '#45617C',
        mirageBlue: '#1B1E3A',
        orange: '#FF7133',
        lightOrange: '#FFA680',
        holidayOrange: '#DD7C23',
        charcoalBlue: '#151d26eb',
        charcoalDark: '#020A19',
        skyBlue: '#45B6FF',
        deepSpaceBlue: '#0F1622',
        deepNightBlue: '#141a25',
        linkBlue: '#0000EE',
        statsOrange: '#FEC800',
        baseWhite: '#F1F1F1',

        // Gray Colors
        veryDarkGray: '#333333',
        midnightBlueGray: '#131C25',
        darkGray: '#757575',
        mediumGray: '#919191',
        gray: '#B2B2B2',
        lightGray: '#E5E5E5',
        veryLightGray: '#F7F7F7',
        veryLightWhiteGray: '#F7F8F9',
        whiteGray: '#68707F',
        blueGray: '#1D293E',
        ironGray: '#CDCFD4',
        navyGray: '#778192',
        slateGray: '#879FB5',
        paleSlate: '#656B77',
        eclipseGray: '#0D1017',
        ebonyClay: '#222B3C',
        bunker: '#0D1518',

        // Red Colors
        lightRed: '#FF4D4D',
        red: '#E60000',
        burntRed: '#430404',
        crimsonRed: '#E11341',

        // Accents
        error: '#FA9999',
        cancel: '#C25454',
        green: '#10A82C',
        lightGreen: '#4BCB34',
        success: '#B7EAAE',

        // accent colors
        accentYellow: '#FFBB00',
        accentBlue: '#00A8F0',
        accent: '#364154',

        // Skeleton Color
        muted: {
          DEFAULT: 'hsl(210 40% 96.1%)',
          foreground: 'hsl(215.4 16.3% 46.9%)',
        },
      },

      // Typography
      fontFamily: {
        Hellix: ['Hellix', 'Helvetica', 'Arial'],
        HellixBlack: ['Hellix Black', 'Helvetica', 'Arial'],
        FoxOneAeroBlack: ['FoxOneAero Black', 'Helvetica', 'Arial'],
        FoxOneAeroBlackItalic: ['FoxOneAero Black Italic', 'Helvetica', 'Arial'],
        FoxOneAeroBold: ['FoxOneAero Bold', 'Helvetica', 'Arial'],
        FoxOneAeroBoldItalic: ['FoxOneAero Bold Italic', 'Helvetica', 'Arial'],
        FoxOneAeroLight: ['FoxOneAero Light', 'Helvetica', 'Arial'],
        FoxOneAeroLightItalic: ['FoxOneAero Light Italic', 'Helvetica', 'Arial'],
        FoxOneAeroMedium: ['FoxOneAero Medium', 'Helvetica', 'Arial'],
        FoxOneAeroMediumItalic: ['FoxOneAero Medium Italic', 'Helvetica', 'Arial'],
        FoxOneAero: ['FoxOneAero', 'Helvetica', 'Arial'],
        FoxOneAeroItalic: ['FoxOneAero Regular Italic', 'Helvetica', 'Arial'],
      },

      boxShadow: {
        '35': '0px 4px 10px 0px rgba(0, 0, 0, 0.35)',
      },

      // Animations
      keyframes: {
        slide: {
          from: {
            '-webkit-transform': 'translate3d(0, 0, 0)',
            transform: 'translate3d(0, 0, 0)',
          },
          to: {
            '-webkit-transform': 'translate3d(calc(-100% - var(--gap-icon-slider)), 0, 0)',
            transform: 'translate3d(calc(-100% - var(--gap-icon-slider)), 0, 0)',
          },
        },
        'accordion-down': {
          from: { height: '0', opacity: '0' },
          to: { height: 'var(--radix-accordion-content-height)', opacity: '1' },
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)', opacity: '1' },
          to: { height: '0', opacity: '0' },
        },
        'end-card-slide': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(0%)' },
        },
      },
      animation: {
        slide: 'slide 22s linear infinite',
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'end-card-slide': 'end-card-slide var(--end-card-timer) linear forwards',
      },
      maxWidth: {
        lg: '1280px',
        coreMaxWidth: '1728px',
      },
      transitionProperty: {
        height: 'height',
      },
      backgroundImage: {
        'gradient-showcase-bunker-to-clear':
          'linear-gradient(90deg, #0D1518 0%, #0D1518 48.44%, rgba(13, 21, 24, 0.00) 62.12%)',
        'gradient-base': 'linear-gradient(180deg, #151B26 0%, #020A19 60.6%)',
        'gradient-header': 'linear-gradient(180deg, #151B26 50%, rgba(21, 27, 38, 0.00) 100%)',
        'centered-spot': 'radial-gradient(50% 50% at 50% 50%, #272D38 0%, rgba(39, 45, 56, 0.00) 100%)',
        'centered-spot-light': 'radial-gradient(50% 50% at 50% 50%, #F1F1F1 0%, rgba(241, 241, 241, 0.00) 100%)',
        'gradient-black-to-light-right': 'linear-gradient(270deg, #000 31.73%, rgba(0, 0, 0, 0.00) 100%)',
        'gradient-black-to-light-left': 'linear-gradient(90deg, #000 31.73%, rgba(0, 0, 0, 0.00) 100%)',
        'gradient-gray-to-light-right': 'linear-gradient(270deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
        'gradient-gray-to-light-left': 'linear-gradient(90deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
        'gradient-dark-to-clear-right': 'linear-gradient(90deg, #000 0.43%, rgba(0, 0, 0, 0.00) 102.47%)',
        'gradient-dark-to-clear-left': 'linear-gradient(270deg, #000 0.43%, rgba(0,0,0,0) 102.47%)',
        'gradient-black-to-clear-top': 'linear-gradient(0, #000 80%, rgba(0,0,0,0) 100%)',
      },
    },
  },
  plugins: [
    tailwindAnimate,
    typography,
    plugin((pluginApi) => {
      const customUtilities = {
        '.no-drag-radix-scroll-area-viewport [data-radix-scroll-area-viewport]': {
          'overscroll-behavior': 'none',
        },
        '.no-clig-lig': {
          'font-feature-settings': "'clig' 0, 'liga' 0",
        },
        '.my-stuff-blur-fill': {
          fill: 'rgba(21, 27, 38, 0.6)',
          'backdrop-filter': 'blur(60px)',
        },
        '.prose span[id]': {
          scrollMarginTop: '80px',
        },
      };

      pluginApi.addUtilities(customUtilities);
    }),
  ],
} satisfies Config;

export default config;
